import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TrendingDown, Target, Rocket, RefreshCw, Globe } from "lucide-react";
import { api } from "../lib/api";
import { fmtMoney, fmtPct, marginColor, displayTicker } from "../lib/format";

const CATEGORIES = [
  {
    key: "most_fallen",
    title: "Most Fallen Today",
    icon: TrendingDown,
    accent: "text-terracotta",
    metric: (r) => ({ label: "Today", value: fmtPct(r.day_change_percent), cls: marginColor(r.day_change_percent) }),
  },
  {
    key: "most_undervalued",
    title: "Most Undervalued",
    icon: Target,
    accent: "text-moss",
    metric: (r) => ({ label: "vs Target", value: fmtPct(r.upside_percent), cls: "text-moss" }),
  },
  {
    key: "top_growth",
    title: "Top Future Growth",
    icon: Rocket,
    accent: "text-amber_brand",
    metric: (r) => ({
      label: r.growth_metric === "revenue" ? "Rev growth" : "EPS growth",
      value: fmtPct(r.growth_percent),
      cls: marginColor(r.growth_percent),
    }),
  },
];

export default function MarketRadar() {
  const [data, setData] = useState(null);
  const [market, setMarket] = useState("IN");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    try {
      const { data: r } = await api.get("/market/radar", { timeout: 120000 });
      setData(r);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  const mkt = data?.markets?.[market];

  return (
    <section
      className="border border-line bg-white rounded-sm overflow-hidden mb-8"
      data-testid="market-radar"
    >
      <header className="px-4 sm:px-5 py-3 sm:py-4 border-b border-line flex items-start justify-between gap-3 bg-gradient-to-br from-bone-100 via-white to-moss-bg/30 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="overline mb-1 flex items-center gap-1.5 text-moss">
            <Globe className="w-3 h-3" />— Daily picks across the whole market
          </div>
          <div className="font-serif-display text-lg sm:text-2xl text-stone_brand-dark font-medium tracking-tight">
            Most undervalued · top growth · biggest falls
          </div>
          <div className="text-[0.7rem] sm:text-xs text-stone_brand mt-1">
            Scanned {mkt?.scanned ?? "—"} {market === "IN" ? "Nifty 50" : "US mega-cap"} names · refreshed every 10 min
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1" data-testid="radar-market-toggle">
            {[
              { key: "IN", label: "India", sub: "IN" },
              { key: "US", label: "US", sub: "US" },
            ].map((m) => (
              <button
                key={m.key}
                onClick={() => setMarket(m.key)}
                className={`px-2.5 sm:px-3 py-1.5 rounded-sm text-xs font-mono tracking-wider transition-colors inline-flex items-center gap-1.5 ${
                  market === m.key
                    ? "bg-stone_brand-dark text-bone-50"
                    : "bg-white border border-line text-stone_brand hover:border-moss/50"
                }`}
                data-testid={`radar-market-${m.key}`}
              >
                <span className="text-[0.6rem] font-semibold opacity-60">{m.sub}</span>
                <span className="hidden sm:inline">{m.label}</span>
              </button>
            ))}
          </div>
          <button
            onClick={() => {
              setRefreshing(true);
              load();
            }}
            disabled={refreshing}
            className="p-1.5 text-stone_brand-light hover:text-moss hover:bg-bone-100 rounded-sm transition-colors"
            data-testid="radar-refresh"
            title="Refresh radar"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
          </button>
        </div>
      </header>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-line">
          {[0, 1, 2].map((i) => (
            <div key={i} className="bg-white p-4 h-52 animate-pulse" />
          ))}
        </div>
      ) : !mkt ? (
        <div className="p-8 text-center text-sm text-stone_brand" data-testid="radar-error">
          Market radar is warming up — check back in a minute.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-line">
          {CATEGORIES.map((cat) => {
            const rows = mkt[cat.key] || [];
            const Icon = cat.icon;
            return (
              <div key={cat.key} className="bg-white" data-testid={`radar-category-${cat.key}`}>
                <div className={`px-4 pt-4 pb-2 flex items-center gap-1.5 overline ${cat.accent}`}>
                  <Icon className="w-3.5 h-3.5" /> {cat.title}
                </div>
                {rows.length === 0 ? (
                  <div className="px-4 pb-5 text-xs text-stone_brand-light">Nothing qualifying right now.</div>
                ) : (
                  <ul>
                    {rows.map((r, i) => {
                      const m = cat.metric(r);
                      return (
                        <li key={r.ticker}>
                          <Link
                            to={`/stock/${encodeURIComponent(r.ticker)}`}
                            className="flex items-center justify-between gap-2 px-4 py-2 hover:bg-bone-100 transition-colors group"
                            data-testid={`radar-item-${cat.key}-${r.ticker}`}
                          >
                            <div className="min-w-0 flex items-baseline gap-2">
                              <span className="font-mono text-[0.6rem] text-stone_brand-light w-4 text-right flex-shrink-0">
                                {i + 1}
                              </span>
                              <div className="min-w-0">
                                <span className="font-mono text-sm font-semibold text-stone_brand-dark group-hover:text-moss transition-colors">
                                  {displayTicker(r.ticker)}
                                </span>
                                <div className="text-[0.65rem] text-stone_brand truncate max-w-[150px]">
                                  {r.company_name}
                                </div>
                              </div>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <div className={`font-mono text-sm font-semibold ${m.cls}`}>{m.value}</div>
                              <div className="text-[0.55rem] font-mono uppercase tracking-widest text-stone_brand-light">
                                {m.label} · {fmtMoney(r.current_price, r.currency)}
                              </div>
                            </div>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="px-5 py-2 border-t border-line bg-bone-50">
        <p className="text-[0.6rem] font-mono uppercase tracking-widest text-stone_brand-light">
          — Fallen picks are drawn only from undervalued / positive-growth names · Undervalued = live price vs Yahoo analyst mean target · Growth = latest earnings/revenue growth · Not investment advice.
        </p>
      </div>
    </section>
  );
}
